'use client';

/**
 * @fileoverview `useCountUp` — animates a number from ~80% of `target` →
 * `target` the moment its element scrolls into view, mirroring the DS v1.5.0
 * city template (`docs/design-system/1.5.0/templates/city/index.html`, JS
 * lines ~873–899) with the WEB-595 start-value raise (no "0" flash).
 *
 * Port of the template's `[data-count]` behaviour:
 *   - `IntersectionObserver` (default `threshold: 0.6`) starts the animation
 *     once per element, then stops observing it.
 *   - ~1100ms ease-out cubic ramp (`1 - (1 - p)^3`) driven by
 *     `requestAnimationFrame`.
 *   - `prefers-reduced-motion: reduce` → snap straight to `target`, no rAF loop.
 *
 * **SSR-safe, no hydration flash.** `value` initialises to `target`, so the
 * server render, the first client (hydration) render, crawlers, and no-JS users
 * all see the real number — the markup matches across the boundary. Only after
 * mount, and only when JS + `IntersectionObserver` are available *and* motion is
 * not reduced, does the element ramp from ~80% of `target` up to `target` on
 * scroll-in (WEB-595 — avoids a perceptible "0" flash on populated stats).
 * The ramp start is a post-commit client state update (when the observer
 * fires), never during hydration, so there is no mismatch.
 */

import { useEffect, useRef, useState } from 'react';

/** Options for {@link useCountUp}. */
export interface UseCountUpOptions {
  /** Final value to count up to. */
  target: number;
  /** Total animation duration in milliseconds. Default `1100`. */
  durationMs?: number;
  /** `IntersectionObserver` visibility threshold that triggers the count-up. Default `0.6`. */
  threshold?: number;
}

/**
 * Return shape of {@link useCountUp}: a `ref` to attach to the element that
 * should be observed, and the current `value` to render inside it.
 *
 * @typeParam T - The element type the `ref` is attached to (defaults to
 * `HTMLElement`), so callers can target a `<span>`, `<div>`, etc. without a
 * variance error.
 */
export interface UseCountUpResult<T extends HTMLElement> {
  ref: React.RefObject<T | null>;
  value: number;
}

const DEFAULT_DURATION_MS = 1100;
const DEFAULT_THRESHOLD = 0.6;
/** Fraction of `target` to start the scroll-in ramp from (WEB-595). */
const COUNT_UP_START_FRACTION = 0.8;

/** Ease-out cubic — matches the DS template (`1 - (1 - p)^3`). */
function easeOutCubic(progress: number): number {
  return 1 - Math.pow(1 - progress, 3);
}

/**
 * Starting value for the animated ramp. Populated stats never flash `0`
 * (WEB-595); a true zero target stays at 0.
 */
export function countUpStartValue(target: number): number {
  if (!Number.isFinite(target) || target <= 0) return 0;
  return Math.max(1, Math.round(target * COUNT_UP_START_FRACTION));
}

/**
 * Animate a number from ~80% of `target` → `target` on scroll-in. SSR-safe and
 * `prefers-reduced-motion` aware.
 *
 * @example
 * ```tsx
 * const { ref, value } = useCountUp({ target: 120 });
 * return <span ref={ref} data-count={120}>{value}</span>;
 * ```
 */
export function useCountUp<T extends HTMLElement = HTMLElement>({
  target,
  durationMs = DEFAULT_DURATION_MS,
  threshold = DEFAULT_THRESHOLD,
}: UseCountUpOptions): UseCountUpResult<T> {
  // Normalise inputs so bad props can't break the animation/observer:
  //   - non-finite or non-positive `durationMs` → would make `progress < 1`
  //     forever, spinning `requestAnimationFrame` indefinitely.
  //   - out-of-range `threshold` → throws when constructing `IntersectionObserver`.
  const safeDurationMs =
    Number.isFinite(durationMs) && durationMs > 0 ? durationMs : DEFAULT_DURATION_MS;
  const safeThreshold = Number.isFinite(threshold)
    ? Math.min(1, Math.max(0, threshold))
    : DEFAULT_THRESHOLD;

  // Initialise to the final value: server + first client render show the real
  // number, so hydration markup matches and crawlers / no-JS users see it too.
  const [value, setValue] = useState<number>(target);
  const ref = useRef<T>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Keep the rendered value in sync with `target`. State is seeded once at
    // mount, so a later `target` prop change would otherwise leave a stale
    // number until an intersection fires (or forever in the reduced-motion /
    // no-observer paths). This runs post-commit, never during hydration.
    setValue(target);

    // No motion → leave the value at `target` (already rendered). Bail before
    // touching `IntersectionObserver` / `requestAnimationFrame`.
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setValue(target);
      return;
    }

    // No IntersectionObserver → cannot detect scroll-in; keep the final value.
    if (typeof IntersectionObserver === 'undefined') return;

    let frame = 0;
    let startTime: number | null = null;

    const now = (): number =>
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now();

    const from = countUpStartValue(target);
    const delta = target - from;

    const step = (): void => {
      if (startTime === null) startTime = now();
      const progress = Math.min((now() - startTime) / safeDurationMs, 1);
      setValue(Math.round(from + delta * easeOutCubic(progress)));
      if (progress < 1) {
        frame = requestAnimationFrame(step);
      } else {
        setValue(target);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            observer.unobserve(entry.target);
            // Start near the target and ramp up (WEB-595). Runs on scroll-in,
            // not during hydration, so there is no SSR mismatch.
            setValue(from);
            startTime = null;
            frame = requestAnimationFrame(step);
            break;
          }
        }
      },
      { threshold: safeThreshold },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [target, safeDurationMs, safeThreshold]);

  return { ref, value };
}
